import { useEffect, useState } from "react";
import { AlertCircle, CalendarDays, FileText, Save, X } from "lucide-react";
import type { CalidadIncidenciaFormValues } from "../types/calidad";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  initialValues: CalidadIncidenciaFormValues;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (values: CalidadIncidenciaFormValues) => Promise<void>;
};

export function CalidadIncidenciaFormModal({
  open,
  mode,
  initialValues,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState(initialValues);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(initialValues);
  }, [initialValues, open]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-slate-950/35 px-4 py-8 backdrop-blur-md">
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[36px] border border-[#dfe8e4] bg-white shadow-[0_44px_120px_rgba(15,23,42,.24)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.05)]"
        >
          <X size={17} />
        </button>

        <div className="grid max-h-[90vh] overflow-y-auto lg:grid-cols-[1.02fr_.98fr]">
          <div className="relative overflow-hidden border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f3fbf7_100%)] p-8 lg:border-b-0 lg:border-r">
            <div className="pointer-events-none absolute -right-24 top-[-120px] h-[280px] w-[280px] rounded-full bg-emerald-300/24 blur-3xl" />
            <div className="pointer-events-none absolute bottom-[-140px] left-[-90px] h-[260px] w-[260px] rounded-full bg-teal-300/14 blur-3xl" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/85 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.08)]">
                <FileText size={14} />
                {mode === "create" ? "Nueva incidencia" : "Editar incidencia"}
              </div>

              <h2 className="mt-5 text-3xl font-black tracking-[-0.045em] text-slate-950 lg:text-4xl">
                {mode === "create"
                  ? "Registrar una incidencia de calidad"
                  : "Actualizar la incidencia seleccionada"}
              </h2>

              <p className="mt-4 max-w-md text-sm font-semibold leading-7 text-slate-500">
                Completa la clasificacion de origen y tipo, el literal y los
                comentarios asociados al registro.
              </p>

              <div className="mt-8 grid gap-4">
                <InfoLine label="Numero" value={String(form.numero)} />
                <InfoLine
                  label="Origen"
                  value={form.q ? "Q" : form.ma ? "MA" : "Sin clasificar"}
                />
                <InfoLine
                  label="Tipo"
                  value={form.ncp ? "NCp" : form.df ? "Df" : "Sin clasificar"}
                />
              </div>
            </div>
          </div>

          <form
            className="space-y-5 bg-white p-8"
            onSubmit={async (event) => {
              event.preventDefault();
              await onSubmit(form);
            }}
          >
            <div>
              <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                Registro
              </div>
              <div className="mt-1 text-xl font-black tracking-tight text-slate-950">
                Datos de la incidencia
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Numero
                </span>
                <input
                  value={form.numero}
                  readOnly
                  className="field-control cursor-not-allowed bg-slate-100"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Fecha
                </span>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="date"
                    value={form.fecha}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        fecha: event.target.value,
                      }))
                    }
                    className="field-control pl-11"
                    required
                  />
                </div>
              </label>

              <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                <TogglePair
                  title="Clasificacion de origen"
                  firstLabel="Q"
                  secondLabel="MA"
                  firstChecked={form.q}
                  secondChecked={form.ma}
                  onFirstChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      q: checked,
                      ma: checked ? false : current.ma,
                    }))
                  }
                  onSecondChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      ma: checked,
                      q: checked ? false : current.q,
                    }))
                  }
                />

                <TogglePair
                  title="Clasificacion de tipo"
                  firstLabel="NCp"
                  secondLabel="Df"
                  firstChecked={form.ncp}
                  secondChecked={form.df}
                  onFirstChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      ncp: checked,
                      df: checked ? false : current.df,
                    }))
                  }
                  onSecondChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      df: checked,
                      ncp: checked ? false : current.ncp,
                    }))
                  }
                />
              </div>

              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Literal
                </span>
                <textarea
                  value={form.literal}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      literal: event.target.value,
                    }))
                  }
                  className="field-control min-h-[140px]"
                  placeholder="Describe la incidencia de calidad."
                  required
                />
              </label>

              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Comentarios para cierre
                </span>
                <textarea
                  value={form.comentarios_para_cierre ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      comentarios_para_cierre: event.target.value,
                    }))
                  }
                  className="field-control min-h-[120px]"
                  placeholder="Observaciones de cierre o seguimiento."
                />
              </label>

              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Comentarios cliente / auditor
                </span>
                <textarea
                  value={form.comentarios_cliente_auditor ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      comentarios_cliente_auditor: event.target.value,
                    }))
                  }
                  className="field-control min-h-[120px]"
                  placeholder="Comentarios del cliente o del auditor."
                />
              </label>
            </div>

            {error && (
              <div className="rounded-[28px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3 border-t border-[#edf1f3] pt-5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={submitting || !form.fecha || !form.literal.trim()}
                className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,#059669,#10b981,#14b8a6)] px-6 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save size={16} />
                {submitting
                  ? "Guardando..."
                  : mode === "create"
                    ? "Crear incidencia"
                    : "Guardar cambios"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TogglePair({
  title,
  firstLabel,
  secondLabel,
  firstChecked,
  secondChecked,
  onFirstChange,
  onSecondChange,
}: {
  title: string;
  firstLabel: string;
  secondLabel: string;
  firstChecked: boolean;
  secondChecked: boolean;
  onFirstChange: (checked: boolean) => void;
  onSecondChange: (checked: boolean) => void;
}) {
  return (
    <div className="rounded-[28px] border border-[#dfe8e4] bg-slate-50/80 p-4">
      <div className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {title}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="calidad-choice-toggle">
          <input
            type="checkbox"
            checked={firstChecked}
            onChange={(event) => onFirstChange(event.target.checked)}
          />
          <span>{firstLabel}</span>
        </label>

        <label className="calidad-choice-toggle">
          <input
            type="checkbox"
            checked={secondChecked}
            onChange={(event) => onSecondChange(event.target.checked)}
          />
          <span>{secondLabel}</span>
        </label>
      </div>
    </div>
  );
}

function InfoLine({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-[#dfe8e4] bg-white/88 px-4 py-3 shadow-[0_10px_26px_rgba(15,23,42,.05)]">
      <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>
      <div className="min-w-0 truncate text-right text-sm font-black text-slate-800">
        {value}
      </div>
    </div>
  );
}
